import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import AppIcon from '../preview/AppIcon'

const t = buildTranslate('live_updates.overview.hero')
const tStages = buildTranslate('live_updates.overview.hero.stages')
const tTracker = buildTranslate('live_updates.preview.tracker')
const tInAppStages = buildTranslate('live_updates.preview.in_app_stages')

const HERO_TRACKER_NODES = ['placed', 'on_the_way', 'delivered'] as const

const ACCENT = palette.belizeHole
const SECONDARY_TEXT = palette.phoneTextMuted
const PHONE_WIDTH = '230px'
const TRACK_STEPS = HERO_TRACKER_NODES.length * 2 - 1

export interface HeroCompactPhoneProps {
  appIconUrl: string | undefined
  merchantName: string | undefined
}

const HeroCompactPhone = ({
  appIconUrl,
  merchantName,
}: HeroCompactPhoneProps) => {
  const [css] = useStyletron()

  return (
    <div
      data-testid="lu-hero-compact-phone"
      className={css({
        boxSizing: 'border-box',
        width: PHONE_WIDTH,
        maxWidth: '100%',
        backgroundImage: `linear-gradient(180deg, ${palette.phoneHeroTop}, ${palette.phoneHeroBottom})`,
        borderTopWidth: '8px',
        borderRightWidth: '8px',
        borderBottomWidth: '8px',
        borderLeftWidth: '8px',
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderTopColor: palette.heroPhoneBezel,
        borderRightColor: palette.heroPhoneBezel,
        borderBottomColor: palette.heroPhoneBezel,
        borderLeftColor: palette.heroPhoneBezel,
        borderTopLeftRadius: '30px',
        borderTopRightRadius: '30px',
        borderBottomLeftRadius: '30px',
        borderBottomRightRadius: '30px',
        paddingTop: '18px',
        paddingRight: '14px',
        paddingBottom: '22px',
        paddingLeft: '14px',
        color: palette.white,
      })}
    >
      <div
        className={css({
          fontSize: '10px',
          color: SECONDARY_TEXT,
          textAlign: 'center',
        })}
      >
        {t('la_date')}
      </div>
      <div
        className={css({
          fontSize: '40px',
          lineHeight: 1.1,
          fontWeight: 900,
          textAlign: 'center',
          marginBottom: '56px',
        })}
      >
        {t('la_clock')}
      </div>
      <div
        className={css({
          backgroundColor: palette.phoneHeroCard,
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          paddingTop: '11px',
          paddingRight: '12px',
          paddingBottom: '11px',
          paddingLeft: '12px',
        })}
      >
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            columnGap: '7px',
            fontSize: '11px',
          })}
        >
          <AppIcon appIconUrl={appIconUrl} size="18px" radius="5px" />
          <span
            className={css({
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {merchantName ?? t('la_brand')}
          </span>
          <span
            className={css({
              marginLeft: 'auto',
              color: SECONDARY_TEXT,
              fontSize: '9px',
              whiteSpace: 'nowrap',
            })}
          >
            {tStages('delivered.eta')}
          </span>
        </div>
        <div
          className={css({
            fontSize: '15px',
            lineHeight: 1.2,
            fontWeight: 900,
            marginTop: '7px',
          })}
        >
          {tInAppStages('delivered')}
        </div>
        <div
          className={css({
            fontSize: '10px',
            color: palette.phoneTextSubtle,
            marginTop: '2px',
            marginBottom: '10px',
          })}
        >
          {tStages('delivered.sub')}
        </div>
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            marginBottom: '4px',
          })}
        >
          {Array.from({ length: TRACK_STEPS }, (_, step) =>
            step % 2 === 0 ? (
              <span
                key={step}
                className={css({
                  display: 'block',
                  flexShrink: 0,
                  width: '8px',
                  height: '8px',
                  borderTopLeftRadius: '50%',
                  borderTopRightRadius: '50%',
                  borderBottomLeftRadius: '50%',
                  borderBottomRightRadius: '50%',
                  backgroundColor: ACCENT,
                })}
              />
            ) : (
              <span
                key={step}
                className={css({
                  display: 'block',
                  flexGrow: 1,
                  height: '2px',
                  backgroundColor: ACCENT,
                })}
              />
            )
          )}
        </div>
        <div
          className={css({
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '8px',
            lineHeight: 1,
            color: SECONDARY_TEXT,
          })}
        >
          {HERO_TRACKER_NODES.map(node => (
            <span key={node}>{tTracker(node)}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

export default HeroCompactPhone
